import type {
  MyPushCameraRows,
  MyPushCategory,
  MyPushChoice,
  MyPushLimits,
  MyPushMode,
  MyPushRequest,
  StoredPushFilters,
} from '@/types'

/**
 * CP 391 — "My phone alerts": the rules shared by the camera box (MyPhoneAlertsSection), the group dialog
 * (GroupMyPushDialog) and the bell's inputs (usePushMutes, PushDeadZones). Pure, no React.
 *
 * The dashboard speaks MODES to the server, never category filters: the server maps a mode to the stored filter map
 * (services/push_modes.py), so mode 3 "Suspicious" could be added without a second copy of the mapping here. #406 — it
 * is, with the switch "Also send Suspicious alerts": sent with every stored choice, shown under "All motion" and
 * "Humans / Animals / Vehicles". The two all-off presets set it themselves: mode 3 on, "Only graffiti" off.
 */

/** The choices in the order the box and the dialog list them; the account setting (the default) first. */
export const MY_PUSH_CHOICES: readonly MyPushChoice[] = ['account', 'all', 'categories', 'suspicious', 'critical_only']

/** #408 — the choices this person sees: the Suspicious preset is for beta users. Without beta a stored one checks nothing,
 *  as a custom choice does. */
export const myPushChoicesFor = (beta: boolean): readonly MyPushChoice[] =>
  beta ? MY_PUSH_CHOICES : MY_PUSH_CHOICES.filter((choice) => choice !== 'suspicious')

/** The three ticks under "Humans / Animals / Vehicles", in the client's order. */
export const MY_PUSH_CATEGORIES: readonly MyPushCategory[] = ['human', 'animal', 'vehicle']

/** A choice as the screen holds it. `mode` may be `custom` only when read back from the server. */
export interface MyPushValue {
  mode: MyPushMode
  categories: MyPushCategory[]
  /** #406 — the Suspicious switch; absent on a server's answer reads as on. */
  suspicious: boolean
}

/** #406 — the presets that show the switch; the others decide it themselves. */
export const myPushShowsSwitch = (mode: MyPushMode | undefined): boolean => mode === 'all' || mode === 'categories'

/** In the client's order, without duplicates or anything unknown. */
const ordered = (categories: readonly string[]): MyPushCategory[] =>
  MY_PUSH_CATEGORIES.filter((c) => categories.includes(c))

/** The request body: the ticked categories for "Humans / Animals / Vehicles", an empty list for every other choice
 *  (the API refuses categories there). #406 — the switch: as set under the two motion presets, on for mode 3, off for
 *  "Only graffiti"; none for "My account setting", which stores nothing. */
export function myPushRequest(value: { mode: MyPushChoice; categories: readonly MyPushCategory[]; suspicious?: boolean }): MyPushRequest {
  const body: MyPushRequest = { mode: value.mode, categories: value.mode === 'categories' ? ordered(value.categories) : [] }
  if (value.mode === 'account') return body
  return { ...body, suspicious: value.mode === 'suspicious' ? true : value.mode === 'critical_only' ? false : value.suspicious !== false }
}

/**
 * What one click makes of the current choice: a choice picked (`mode`) or a tick toggled (`toggle`). null = nothing
 * to save (the same choice again, or the last tick, which must stay: at least one type stays ticked).
 * Picking "Humans / Animals / Vehicles" from another choice ticks all three; picking it again keeps the saved ticks.
 * `current` null = nothing chosen yet (the group dialog opens so): every choice is a change.
 * #406 — `suspicious` moves the switch, only under the two motion presets. Moving between them keeps it; coming from
 * anywhere else it reads on again, its default.
 */
export function nextMyPushValue(
  current: MyPushValue | null,
  action: { mode: MyPushChoice } | { toggle: MyPushCategory } | { suspicious: boolean },
): (MyPushValue & { mode: MyPushChoice }) | null {
  if ('suspicious' in action) {
    if (!current || !myPushShowsSwitch(current.mode) || current.suspicious === action.suspicious) return null
    return { ...current, mode: current.mode as MyPushChoice, suspicious: action.suspicious }
  }
  if ('toggle' in action) {
    if (current?.mode !== 'categories') return null
    const has = current.categories.includes(action.toggle)
    if (has && current.categories.length <= 1) return null
    const next = has ? current.categories.filter((c) => c !== action.toggle) : [...current.categories, action.toggle]
    return { mode: 'categories', categories: ordered(next), suspicious: current.suspicious }
  }
  if (action.mode === current?.mode) return null
  const kept = current && myPushShowsSwitch(current.mode) ? current.suspicious : true
  if (action.mode === 'categories') return { mode: 'categories', categories: [...MY_PUSH_CATEGORIES], suspicious: kept }
  if (action.mode === 'all') return { mode: 'all', categories: [], suspicious: kept }
  return { mode: action.mode, categories: [], suspicious: action.mode !== 'critical_only' }
}

/** The box's state as read from GET /cameras/{id}/my-push/. */
export interface MyPushLimitState {
  mode: MyPushMode
  categories: MyPushCategory[]
  in_audience: boolean
  limits: MyPushLimits
}

/** The keys under settings `myPush.limits`. */
export type MyPushLimitKey =
  | 'notAudience' | 'criticalOff' | 'myPushOff' | 'cameraPushOff' | 'cameraLimited' | 'cameraLimitedNone'
  | 'notAudienceNoCritical' | 'myPushOffNoCritical' | 'cameraPushOffNoCritical'

/**
 * The one line the box shows under the choice, when something outside it changes what arrives — so the screen never
 * promises alerts that will not come. The first that applies, most decisive first:
 *   - not one of the camera's users (a superuser on someone else's camera): read-only, only graffiti reaches them;
 *   - "Only graffiti" while graffiti detection is off for the camera: nothing at all will arrive. Graffiti (critical)
 *     alerts pass the push switches and the camera filter, so no other line applies to "Only graffiti";
 *   - the person's own "push to my devices" switch is off: only graffiti alerts;
 *   - the camera owner's push switch is off: only graffiti alerts;
 *   - the camera owner's filter leaves out a ticked type ("All motion" ticks all three): what it still lets through.
 *     Not for "My account setting": the account categories are not shown in this box. #406 — "Suspicious + graffiti"
 *     ticks humans: a Suspicious event always has a person, and a filter that leaves out humans drops it first.
 * The three "only graffiti" lines (not a user, own switch off, camera switch off) take their `...NoCritical` form when
 * graffiti detection is off for the camera (`critical_enabled` false): then no phone alerts arrive from it at all.
 */
export function myPushLimitLine(state: MyPushLimitState): { key: MyPushLimitKey; categories?: MyPushCategory[] } | null {
  const { limits } = state
  const noCritical = limits.critical_enabled === false
  if (!state.in_audience) return { key: noCritical ? 'notAudienceNoCritical' : 'notAudience' }
  if (state.mode === 'critical_only') return noCritical ? { key: 'criticalOff' } : null
  if (limits.my_push_enabled === false) return { key: noCritical ? 'myPushOffNoCritical' : 'myPushOff' }
  if (limits.camera_push_enabled === false) return { key: noCritical ? 'cameraPushOffNoCritical' : 'cameraPushOff' }

  const wanted: readonly MyPushCategory[] = state.mode === 'all' ? MY_PUSH_CATEGORIES
    : state.mode === 'categories' ? state.categories
    : state.mode === 'suspicious' ? ['human']
    : []
  const allowed = limits.camera_categories
  if (!Array.isArray(allowed)) return null   // an answer without it never claims a limit
  if (!wanted.some((c) => !allowed.includes(c))) return null
  const through = ordered(allowed)
  return through.length > 0 ? { key: 'cameraLimited', categories: through } : { key: 'cameraLimitedNone' }
}

/** The bell's per-camera input: camera id -> the stored filter map. null while the list is not loaded. */
export type MyCameraFilters = Record<number, StoredPushFilters>

/** GET /push-preferences/cameras/ as the bell reads it: each camera row's stored filters, exactly as returned
 *  (the `_mode` marker included; the matcher ignores it). undefined (not loaded yet) -> null. */
export function myCameraFiltersOf(rows: MyPushCameraRows | undefined): MyCameraFilters | null {
  if (!rows) return null
  const out: MyCameraFilters = {}
  for (const [id, row] of Object.entries(rows.cameras ?? {})) {
    const cameraId = Number(id)
    if (Number.isInteger(cameraId) && row && typeof row.filters === 'object' && row.filters !== null) {
      out[cameraId] = row.filters
    }
  }
  return out
}

/** The modes of the person's camera rows, by camera id (PushDeadZones skips "Only graffiti" rows). */
export function myCameraModesOf(rows: MyPushCameraRows | undefined): Record<number, MyPushMode> {
  const out: Record<number, MyPushMode> = {}
  for (const [id, row] of Object.entries(rows?.cameras ?? {})) {
    const cameraId = Number(id)
    if (Number.isInteger(cameraId) && row) out[cameraId] = row.mode
  }
  return out
}
